import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Brain, MapPin, TrendingUp, Home, GraduationCap, Bus, ShoppingBag,
  TreePine, UtensilsCrossed, Heart, Loader2, RefreshCw, ChevronDown,
  ChevronUp, Zap, AlertTriangle, Target, BarChart3, DollarSign,
  Building2, ArrowUpRight, ArrowDownRight, Minus, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { MapContainer, TileLayer, CircleMarker, Popup, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  schools: GraduationCap,
  transit: Bus,
  shopping: ShoppingBag,
  parks: TreePine,
  restaurants: UtensilsCrossed,
  healthcare: Heart,
};

const CATEGORY_COLORS = {
  schools: 'text-blue-500 bg-blue-500/10',
  transit: 'text-purple-500 bg-purple-500/10',
  shopping: 'text-orange-500 bg-orange-500/10',
  parks: 'text-green-500 bg-green-500/10',
  restaurants: 'text-red-500 bg-red-500/10',
  healthcare: 'text-pink-500 bg-pink-500/10',
};

const MAP_COLORS = {
  schools: '#3B82F6', transit: '#8B5CF6', shopping: '#F97316',
  parks: '#22C55E', restaurants: '#EF4444', healthcare: '#EC4899',
};

// Thresholds for star rating per category (tuned for 1km radius urban Quebec)
const STAR_THRESHOLDS = {
  schools:     [1, 2, 4, 8],
  transit:     [5, 20, 50, 100],
  shopping:    [5, 20, 50, 100],
  parks:       [2, 5, 10, 20],
  restaurants: [5, 15, 40, 80],
  healthcare:  [1, 3, 6, 10],
};

function getStars(category, count) {
  const t = STAR_THRESHOLDS[category] || [1, 3, 6, 10];
  if (count >= t[3]) return 5;
  if (count >= t[2]) return 4;
  if (count >= t[1]) return 3;
  if (count >= t[0]) return 2;
  return count > 0 ? 1 : 0;
}

const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [20, 33], iconAnchor: [10, 33], popupAnchor: [0, -30], shadowSize: [33, 33],
});

function MarketDirectionIcon({ direction }) {
  if (direction === 'rising') return <ArrowUpRight className="w-4 h-4 text-green-500" />;
  if (direction === 'declining') return <ArrowDownRight className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-amber-500" />;
}

function ScoreRing({ score, size = 100, strokeWidth = 6, label }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const color = score >= 75 ? '#45C04A' : score >= 50 ? '#F59E0B' : '#EF4444';

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg className="-rotate-90" width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke="currentColor" className="text-slate-100 dark:text-slate-800" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`}
          style={{ filter: `drop-shadow(0 0 6px ${color}55)`, transition: 'stroke-dasharray 0.8s ease' }} />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-2xl font-black" style={{ color }}>{score}</span>
        {label && <span className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">{label}</span>}
      </div>
    </div>
  );
}

function ScoreBar({ label, score, weight }) {
  const color = score >= 75 ? '#45C04A' : score >= 50 ? '#F59E0B' : '#EF4444';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-slate-500 dark:text-slate-400">{label}</span>
        <span className="font-semibold" style={{ color }}>{score}/100 <span className="text-slate-400 font-normal">({weight}%)</span></span>
      </div>
      <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

export default function PropertyIntelligencePanel({ address, dealId, isPro }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({ amenities: false, market: false, ai: true });

  const loadIntelligence = async (forceRefresh = false) => {
    if (!address || !isPro) return;
    setLoading(true);
    setError(null);
    const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/property/intelligence`, {
        address,
        deal_id: dealId || null,
        force_refresh: forceRefresh,
        language: lang,
      }, { withCredentials: true });
      setData(resp.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('pro_required');
      } else {
        setError('fetch_failed');
        console.error('Intelligence fetch failed:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (address && isPro) {
      loadIntelligence();
    }
     
  }, [address, isPro]);

  const toggle = (section) => setExpanded(prev => ({ ...prev, [section]: !prev[section] }));

  if (!isPro) {
    return (
      <div data-testid="intelligence-panel-locked" className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#45C04A] to-[#65D95A] rounded-xl flex items-center justify-center">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-primary font-bold text-lg text-[#12355B] dark:text-white">{t('intelligence.title')}</h3>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{t('intelligence.proRequired')}</p>
        <Button onClick={() => window.location.href = '/pricing'} className="bg-[#45C04A] hover:bg-[#65D95A] text-white rounded-xl w-full">
          {t('intelligence.upgradePro')}
        </Button>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div data-testid="intelligence-panel-loading" className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-8">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-[#45C04A] to-[#65D95A] rounded-xl flex items-center justify-center animate-pulse">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-[#45C04A]" />
            <span className="text-sm text-slate-500 dark:text-slate-400">{t('intelligence.analyzing')}</span>
          </div>
          <p className="text-xs text-slate-400 text-center">{t('intelligence.analyzingDesc')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="intelligence-panel-error" className="bg-white dark:bg-[#131824] border border-red-200 dark:border-red-800/30 rounded-2xl p-6">
        <div className="text-center">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">{t('intelligence.fetchFailed')}</p>
          <Button onClick={() => loadIntelligence()} variant="outline" size="sm" className="rounded-xl">
            <RefreshCw className="w-3 h-3 mr-1" /> {t('intelligence.retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const inv = data.investment_score || {};
  const amenities = data.amenities || {};
  const market = data.neighborhood_intelligence || {};
  const ai = data.ai_summary || {};
  const demographics = market.demographics || {};
  const realEstate = market.real_estate_market || {};
  const trend = market.market_trend || {};

  return (
    <div data-testid="intelligence-panel" className="space-y-4">
      {/* Header with RendPleX Score */}
      <div className="bg-gradient-to-br from-[#12355B] to-[#1565C0] rounded-2xl p-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(0,200,83,0.4) 0%, transparent 50%)',
        }} />
        <div className="relative flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Brain className="w-5 h-5 text-[#45C04A]" />
              <h3 className="font-primary font-bold text-base">{t('intelligence.title')}</h3>
            </div>
            <p className="text-white/60 text-xs mb-4">{t('intelligence.poweredBy')}</p>
            <div className="flex items-baseline gap-1.5">
              <span className="text-5xl font-black text-[#45C04A]">{inv.overall || 0}</span>
              <span className="text-white/40 text-sm font-semibold">/100</span>
            </div>
            <p className="text-sm font-medium text-white/80 mt-1">{inv.recommendation || ''}</p>
          </div>
          <ScoreRing score={inv.overall || 0} size={90} strokeWidth={5} />
        </div>
        <button
          data-testid="intelligence-refresh-btn"
          onClick={() => loadIntelligence(true)}
          disabled={loading}
          className="absolute top-4 right-4 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 text-white/70 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Score Breakdown */}
      <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-3">
        <h4 className="font-primary font-bold text-sm text-[#12355B] dark:text-white">{t('intelligence.scoreBreakdown')}</h4>
        <ScoreBar label={t('intelligence.cashFlowScore')} score={inv.cash_flow_score || 0} weight={30} />
        <ScoreBar label={t('intelligence.locationScore')} score={inv.location_score || 0} weight={25} />
        <ScoreBar label={t('intelligence.marketScore')} score={inv.market_score || 0} weight={25} />
        <ScoreBar label={t('intelligence.propertyScore')} score={inv.property_score || 0} weight={20} />
      </div>

      {/* AI Insights */}
      <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
        <button onClick={() => toggle('ai')} className="w-full flex items-center justify-between p-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#45C04A]" />
            <h4 className="font-primary font-bold text-sm text-[#12355B] dark:text-white">{t('intelligence.aiInsights')}</h4>
          </div>
          {expanded.ai ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {expanded.ai && (
          <div className="px-5 pb-5 space-y-4">
            {ai.summary && (
              <p data-testid="intelligence-ai-summary" className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{ai.summary}</p>
            )}
            {ai.strengths?.length > 0 && (
              <div>
                <h5 className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider mb-2">{t('intelligence.strengths')}</h5>
                <ul className="space-y-1">
                  {ai.strengths.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                      <ArrowUpRight className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />{s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {ai.risks?.length > 0 && (
              <div>
                <h5 className="text-xs font-semibold text-red-500 dark:text-red-400 uppercase tracking-wider mb-2">{t('intelligence.risks')}</h5>
                <ul className="space-y-1">
                  {ai.risks.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />{r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {ai.strategy && (
              <div className="bg-[#12355B]/5 dark:bg-[#45C04A]/5 rounded-xl p-3">
                <h5 className="text-xs font-semibold text-[#12355B] dark:text-[#45C04A] uppercase tracking-wider mb-1">{t('intelligence.strategy')}</h5>
                <p data-testid="intelligence-ai-strategy" className="text-sm text-slate-600 dark:text-slate-300">{ai.strategy}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Market Analysis */}
      <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
        <button onClick={() => toggle('market')} className="w-full flex items-center justify-between p-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#45C04A]" />
            <h4 className="font-primary font-bold text-sm text-[#12355B] dark:text-white">{t('intelligence.marketAnalysis')}</h4>
          </div>
          {expanded.market ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {expanded.market && (
          <div className="px-5 pb-5 space-y-4">
            {/* Real Estate Market */}
            <div className="grid grid-cols-2 gap-3">
              {realEstate.average_sale_price && (
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">{t('intelligence.avgSalePrice')}</p>
                  <p className="text-sm font-bold text-[#12355B] dark:text-white">${Number(realEstate.average_sale_price).toLocaleString()}</p>
                </div>
              )}
              {realEstate.average_rent && (
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">{t('intelligence.avgRent')}</p>
                  <p className="text-sm font-bold text-[#12355B] dark:text-white">${Number(realEstate.average_rent).toLocaleString()}/mo</p>
                </div>
              )}
              {realEstate.price_per_square_foot && (
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">{t('intelligence.pricePerSqft')}</p>
                  <p className="text-sm font-bold text-[#12355B] dark:text-white">${Number(realEstate.price_per_square_foot).toLocaleString()}</p>
                </div>
              )}
              {realEstate.rental_demand_score && (
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">{t('intelligence.rentalDemand')}</p>
                  <p className="text-sm font-bold text-[#12355B] dark:text-white">{realEstate.rental_demand_score}/10</p>
                </div>
              )}
            </div>
            {/* Market Trend */}
            {trend.market_direction && (
              <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
                <div className="flex items-center gap-2">
                  <MarketDirectionIcon direction={trend.market_direction} />
                  <span className="text-sm text-slate-600 dark:text-slate-300">{t('intelligence.marketTrend')}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold text-[#12355B] dark:text-white capitalize">{trend.market_direction}</span>
                  {trend.appreciation_rate && (
                    <span className="text-xs text-slate-400 ml-1.5">+{trend.appreciation_rate}%/yr</span>
                  )}
                </div>
              </div>
            )}
            {/* Demographics */}
            {demographics.average_income && (
              <div>
                <h5 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{t('intelligence.demographics')}</h5>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-sm"><span className="text-slate-400">{t('intelligence.avgIncome')}:</span> <span className="font-semibold text-[#12355B] dark:text-white">${Number(demographics.average_income).toLocaleString()}</span></div>
                  {demographics.population && <div className="text-sm"><span className="text-slate-400">{t('intelligence.population')}:</span> <span className="font-semibold text-[#12355B] dark:text-white">{Number(demographics.population).toLocaleString()}</span></div>}
                </div>
              </div>
            )}
            <p className="text-[10px] text-slate-400 italic">{t('intelligence.aiEstimateNote')}</p>
          </div>
        )}
      </div>

      {/* Amenity Intelligence — Investor-Friendly */}
      <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
        <button onClick={() => toggle('amenities')} className="w-full flex items-center justify-between p-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#45C04A]" />
            <h4 className="font-primary font-bold text-sm text-[#12355B] dark:text-white">{t('intelligence.amenityIntelligence')}</h4>
            {amenities.location_score > 0 && (
              <span className="ml-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-[#45C04A]/10 text-[#45C04A]">
                {amenities.location_score}/100
              </span>
            )}
          </div>
          {expanded.amenities ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {expanded.amenities && (
          <div className="px-5 pb-5 space-y-4">
            {/* Star Ratings Grid */}
            <div data-testid="amenity-ratings" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(CATEGORY_ICONS).map(([cat, Icon]) => {
                const catData = amenities[cat] || {};
                const stars = getStars(cat, catData.count || 0);
                return (
                  <div key={cat} data-testid={`amenity-${cat}`} className="flex items-start gap-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${CATEGORY_COLORS[cat]}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs font-semibold text-[#12355B] dark:text-white">{t(`intelligence.cat_${cat}`)}</span>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(i => (
                            <Star key={i} className={`w-3 h-3 ${i <= stars ? 'text-amber-400 fill-amber-400' : 'text-slate-200 dark:text-slate-700'}`} />
                          ))}
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        {t(`amenity.rating_${cat}_${Math.min(stars, 5)}`, { count: catData.count || 0 })}
                      </p>
                      {catData.nearest_distance != null && (
                        <p className="text-[10px] text-slate-400 mt-0.5">{t('amenity.nearest')}: {catData.nearest_distance}m</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Nearby Amenities Mini-Map */}
            {data.latitude && data.longitude && (
              <div data-testid="amenity-map" className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700" style={{ height: 220 }}>
                <MapContainer
                  center={[data.latitude, data.longitude]}
                  zoom={15}
                  style={{ height: '100%', width: '100%' }}
                  zoomControl={false}
                  scrollWheelZoom={false}
                  attributionControl={false}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <Marker position={[data.latitude, data.longitude]} icon={redIcon}>
                    <Popup>{data.address || address}</Popup>
                  </Marker>
                  {Object.entries(CATEGORY_ICONS).map(([cat]) => {
                    const catData = amenities[cat] || {};
                    const count = catData.count || 0;
                    if (!count || !data.latitude) return null;
                    // Generate approximate positions around property for visual representation
                    const pts = [];
                    const rng = (s) => (Math.sin(s * 12.9898 + 78.233) * 43758.5453) % 1;
                    for (let i = 0; i < Math.min(count, 8); i++) {
                      const angle = (rng(i + cat.charCodeAt(0)) * Math.PI * 2);
                      const dist = 0.002 + rng(i * 7 + cat.charCodeAt(1)) * 0.006;
                      pts.push([data.latitude + Math.cos(angle) * dist, data.longitude + Math.sin(angle) * dist]);
                    }
                    return pts.map((p, i) => (
                      <CircleMarker key={`${cat}-${i}`} center={p} radius={4}
                        pathOptions={{ color: MAP_COLORS[cat], fillColor: MAP_COLORS[cat], fillOpacity: 0.7, weight: 1 }}>
                        <Popup><span className="text-xs font-medium capitalize">{t(`intelligence.cat_${cat}`)}</span></Popup>
                      </CircleMarker>
                    ));
                  })}
                </MapContainer>
              </div>
            )}

            {/* Map Legend */}
            <div className="flex flex-wrap gap-2">
              {Object.entries(CATEGORY_ICONS).map(([cat, Icon]) => (
                <div key={cat} className="flex items-center gap-1.5 text-[10px] text-slate-500">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: MAP_COLORS[cat] }} />
                  {t(`intelligence.cat_${cat}`)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Location Info */}
      {data.distance_to_center && (
        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 flex items-center justify-between text-sm">
          <span className="text-slate-400">{t('intelligence.distanceToCenter')}</span>
          <span className="font-semibold text-[#12355B] dark:text-white">{data.distance_to_center} km</span>
        </div>
      )}

      {/* Data Sources */}
      {data.data_sources?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {data.data_sources.map(src => (
            <span key={src} className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
              {src}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
